import {
  IssueDefaultsConfig,
  IssueDefaultsOverride,
} from "../agent-config-options";

/**
 * Valid `status:*` values that may appear in an
 * `IssueDefaultsOverride.status`. The list mirrors the canonical
 * status taxonomy documented in the `issue-label-conventions` rule
 * — every value here renders as a `status:<value>` label on the
 * downstream `gh issue create` invocation.
 *
 * `deferred` is included so consumers can route low-priority
 * downstream byproducts (e.g. `people:research` follow-ups in a
 * research-heavy planning repo) into a manually-promoted backlog
 * instead of the auto-dispatch queue.
 */
export const VALID_STATUS_VALUES = [
  "ready",
  "blocked",
  "in-progress",
  "ready-for-review",
  "needs-attention",
  "done",
  "deferred",
] as const;

export type IssueDefaultsStatus = (typeof VALID_STATUS_VALUES)[number];

/**
 * Valid `priority:*` values that may appear in an
 * `IssueDefaultsOverride.priority`. Mirrors the five-level priority
 * taxonomy documented in the `issue-label-conventions` rule.
 */
export const VALID_PRIORITY_VALUES = [
  "critical",
  "high",
  "medium",
  "low",
  "trivial",
] as const;

export type IssueDefaultsPriority = (typeof VALID_PRIORITY_VALUES)[number];

/**
 * Bundle-shipped defaults used when no `issueDefaults[<phase>]`
 * override is configured. `ready` / `medium` matches the historical
 * hardcoded values every bundle carried before this knob existed.
 */
export const DEFAULT_ISSUE_STATUS: IssueDefaultsStatus = "ready";
export const DEFAULT_ISSUE_PRIORITY: IssueDefaultsPriority = "medium";

/**
 * Fully-resolved per-phase-label entry. Every field is filled in so
 * downstream callers can render `status:<value>` / `priority:<value>`
 * label lines without re-checking for `undefined`.
 */
export interface ResolvedIssueDefaultsEntry {
  readonly status: IssueDefaultsStatus;
  readonly priority: IssueDefaultsPriority;
}

/**
 * Fully-resolved issue-defaults configuration. The `overrides` map
 * is keyed by phase label (e.g. `people:research`, `company:draft`,
 * `req:write`) — not by `type:*` — so consumers can discriminate per
 * phase. The `defaults` field carries the bundle-shipped fallback
 * values (`ready` / `medium`) callers fall back to when no override
 * is configured for a given phase.
 */
export interface ResolvedIssueDefaults {
  readonly defaults: ResolvedIssueDefaultsEntry;
  readonly overrides: Readonly<Record<string, ResolvedIssueDefaultsEntry>>;
}

/**
 * Default-everything resolved instance. Used by bundle factories
 * when the consumer supplies no override at all (the common case).
 */
export const DEFAULT_RESOLVED_ISSUE_DEFAULTS: ResolvedIssueDefaults = {
  defaults: {
    status: DEFAULT_ISSUE_STATUS,
    priority: DEFAULT_ISSUE_PRIORITY,
  },
  overrides: {},
};

/**
 * Resolve a (possibly absent) `IssueDefaultsConfig` into a canonical
 * `ResolvedIssueDefaults` with every override entry filled in.
 *
 * Each entry inside the consumer-supplied map may set `status`,
 * `priority`, or both. Missing fields cascade from the bundle
 * defaults (`ready` / `medium`) so a partially-specified entry
 * still resolves to a complete pair.
 *
 * Malformed configs throw a descriptive `Error`:
 *
 * - Empty / whitespace-only phase-label key.
 * - `status` set to a value outside `VALID_STATUS_VALUES`.
 * - `priority` set to a value outside `VALID_PRIORITY_VALUES`.
 * - Empty entry (neither `status` nor `priority` supplied) — the
 *   override would be a no-op and probably indicates a typo on the
 *   field name.
 */
export function resolveIssueDefaults(
  config?: IssueDefaultsConfig,
): ResolvedIssueDefaults {
  if (config === undefined) {
    return DEFAULT_RESOLVED_ISSUE_DEFAULTS;
  }

  const overrides: Record<string, ResolvedIssueDefaultsEntry> = {};
  for (const [phaseLabel, override] of Object.entries(config)) {
    assertValidPhaseLabel(phaseLabel);
    assertValidOverride(phaseLabel, override);
    overrides[phaseLabel] = {
      status: override.status ?? DEFAULT_ISSUE_STATUS,
      priority: override.priority ?? DEFAULT_ISSUE_PRIORITY,
    };
  }

  return {
    defaults: {
      status: DEFAULT_ISSUE_STATUS,
      priority: DEFAULT_ISSUE_PRIORITY,
    },
    overrides,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when
 * the supplied `IssueDefaultsConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * override fails the build instead of silently shipping unrecognised
 * label values into bundle prompts. Returns the resolved config so
 * callers can write `const id = validateIssueDefaultsConfig(config)`
 * in one line.
 */
export function validateIssueDefaultsConfig(
  config?: IssueDefaultsConfig,
): ResolvedIssueDefaults {
  return resolveIssueDefaults(config);
}

/**
 * Look up the effective `status` / `priority` pair for a phase label.
 * Returns the override entry when one is configured for the given
 * phase, otherwise the bundle defaults (`ready` / `medium`).
 *
 * The single canonical helper bundle prompt templates call when they
 * need to render the labels for a downstream filing — keeps every
 * filing site in sync with the same resolution rule.
 */
export function labelsForPhase(
  resolved: ResolvedIssueDefaults,
  phaseLabel: string,
): ResolvedIssueDefaultsEntry {
  return resolved.overrides[phaseLabel] ?? resolved.defaults;
}

function assertValidPhaseLabel(phaseLabel: string): void {
  if (typeof phaseLabel !== "string" || phaseLabel.trim() === "") {
    throw new Error(
      "AgentConfigOptions.issueDefaults: phase-label keys must be " +
        "non-empty strings (e.g. `people:research`).",
    );
  }
}

function assertValidOverride(
  phaseLabel: string,
  override: IssueDefaultsOverride,
): void {
  if (
    override === null ||
    typeof override !== "object" ||
    Array.isArray(override)
  ) {
    throw new Error(
      `AgentConfigOptions.issueDefaults["${phaseLabel}"] must be an ` +
        "object with optional `status` and `priority` fields.",
    );
  }

  const { status, priority } = override;

  if (status === undefined && priority === undefined) {
    throw new Error(
      `AgentConfigOptions.issueDefaults["${phaseLabel}"] must declare ` +
        "at least one of `status` or `priority`. Empty entries are " +
        "rejected because they are almost always a typo on the field name.",
    );
  }

  if (status !== undefined && !VALID_STATUS_VALUES.includes(status)) {
    throw new Error(
      `AgentConfigOptions.issueDefaults["${phaseLabel}"].status="${status}" ` +
        `is not a recognised status value. Allowed values: ${VALID_STATUS_VALUES.join(", ")}.`,
    );
  }

  if (priority !== undefined && !VALID_PRIORITY_VALUES.includes(priority)) {
    throw new Error(
      `AgentConfigOptions.issueDefaults["${phaseLabel}"].priority="${priority}" ` +
        `is not a recognised priority value. Allowed values: ${VALID_PRIORITY_VALUES.join(", ")}.`,
    );
  }
}
